import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RadioGroup as RadioButton, Select, Flex, Text } from '@gravity-ui/uikit';
import { toastError } from '../../libs/toasts/toasts';
import { ClientPasteParams, PasteTypes, ServerPasteParams } from './types/types';
import { VideosService } from '../../api/api-services/videos-service/videosService';
import { InsertionCodeField } from './components/InsertionCodeField';
import { InsertionInfo } from './components/InsertionInfo';
import { pasteTypesRadio } from './constants/pasteTypesRadio';
import { PasteParamsSettings } from './components/PasteParamsSettings';
import { Player, PlayerHandle } from './components/Player';
import { vzhikLogger } from '../../libs/vzhik-logger/VzhikLogger';
import { adjustIframeSizes } from './utils/adjustIframeSizes';
import { sharePanelT, toastsT } from '../../i18n';

type SharePanelProps = {
    contentId: string;
    isMobile?: boolean;
};

export function SharePanel({ contentId, isMobile }: SharePanelProps) {
    const [loading, setLoading] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<PasteTypes>('wikiMarkdown');
    const playerRef = useRef<PlayerHandle>(null);

    const [showAllVariants, setShowAllVariants] = useState<boolean>(false);

    useEffect(() => {
        const checkScreenSize = () => {
            setShowAllVariants(window.innerHeight > 900);
        };

        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);

        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    // Код вставки с параметрами, контролирующиеся сервером
    const [codeWithServerParams, setCodeWithServerParams] = useState<Record<PasteTypes, string>>({
        playerUrl: '',
        iframe: '',
        wiki: '',
        wikiMarkdown: '',
    });
    const [serverParams, setServerParams] = useState<Record<ServerPasteParams, boolean>>({
        autoplay: false,
        mute: false,
    });
    useEffect(() => {
        if (!contentId) {
            return;
        }

        (async () => {
            try {
                setLoading(true);

                const {
                    iframe,
                    wiki,
                    player_url: playerUrl,
                    wiki_markdown: wikiMarkdown,
                } = await VideosService.getPlayerUrl(contentId, serverParams);

                setCodeWithServerParams({
                    iframe,
                    wiki,
                    playerUrl,
                    wikiMarkdown,
                });
            } catch (e) {
                toastError.open(toastsT('dataFetchFailed'));
                console.error('Не удалось получить данные по причине', e);
                vzhikLogger.logError({
                    message: 'Не удалось получить данные',
                    type: 'server',
                    additional: {
                        error: e,
                    },
                });
            } finally {
                setLoading(false);
            }
        })();
    }, [contentId, serverParams]);

    // Параметры, контролирующиеся на клиенте
    const [clientParams, setClientParams] = useState<ClientPasteParams>({
        preload: '1',
    });

    const [videoSize, setVideoSize] = useState<{ width: number; height: number } | null>(null);

    const handleVideoSizeChange = useCallback((width: number, height: number) => {
        setVideoSize({ width, height });
    }, []);

    // Финальный код вставки
    const [codeWithAllParams, setCodeWithAllParams] = useState<Record<PasteTypes, string>>({
        playerUrl: '',
        iframe: '',
        wiki: '',
        wikiMarkdown: '',
    });
    useEffect(() => {
        if (!codeWithServerParams.playerUrl) {
            return;
        }

        const { playerUrl, iframe, wiki, wikiMarkdown } = codeWithServerParams;
        const clientSearchParams = new URLSearchParams({ preload: clientParams.preload });
        if (clientParams.t !== undefined) {
            clientSearchParams.set('t', clientParams.t);
        }
        const playerUrlWithAllParams = `${playerUrl}&${clientSearchParams.toString()}`;

        let codes = {
            playerUrl: playerUrlWithAllParams,
            iframe: iframe.replace(playerUrl, playerUrlWithAllParams),
            wiki: wiki.replace(playerUrl, playerUrlWithAllParams),
            wikiMarkdown: wikiMarkdown.replace(playerUrl, playerUrlWithAllParams),
        };

        if (videoSize) {
            codes = adjustIframeSizes(codes, videoSize.width, videoSize.height) as Record<
                PasteTypes,
                string
            >;
        }

        setCodeWithAllParams(codes);
    }, [codeWithServerParams, clientParams, videoSize]);

    const playerUrl = useMemo(
        () => `${codeWithServerParams.playerUrl.split('?')[0]}?autoplay=0&preload=1`,
        [codeWithServerParams.playerUrl],
    );

    const handleSeekTime = useCallback((timeInSeconds: number) => {
        if (playerRef.current) {
            playerRef.current.seekTo(timeInSeconds);
        }
    }, []);

    const selectOptions = useMemo(
        () =>
            pasteTypesRadio.map((item) => ({
                value: item.value,
                content: sharePanelT(item.i18nKey),
            })),
        [],
    );

    return (
        <div
            style={{
                padding: isMobile ? '16px' : '20px',
            }}
        >
            <Flex direction="column" gap="4">
                <PasteParamsSettings
                    serverParams={serverParams}
                    setServerParams={setServerParams}
                    clientParams={clientParams}
                    setClientParams={setClientParams}
                    isMobile={isMobile}
                    onSeekTime={handleSeekTime}
                />

                {!isMobile && (
                    <Player
                        ref={playerRef}
                        loading={!codeWithServerParams.playerUrl}
                        playerUrl={playerUrl}
                        onVideoSizeChange={handleVideoSizeChange}
                    />
                )}

                {showAllVariants ? (
                    <Flex direction="column" gap="5">
                        {pasteTypesRadio.map((item) => (
                            <Flex key={item.value} direction="column" gap="2">
                                <Text variant="subheader-2">{sharePanelT(item.i18nKey)}</Text>
                                <InsertionInfo activeTab={item.value} />
                                <InsertionCodeField
                                    loading={loading}
                                    text={codeWithAllParams[item.value]}
                                />
                            </Flex>
                        ))}
                    </Flex>
                ) : (
                    <>
                        {isMobile ? (
                            <Select
                                size="xl"
                                value={[activeTab]}
                                options={selectOptions}
                                onUpdate={(v) => setActiveTab(v[0] as PasteTypes)}
                                width="max"
                            />
                        ) : (
                            <RadioButton
                                value={activeTab}
                                options={selectOptions}
                                onUpdate={(v) => setActiveTab(v as PasteTypes)}
                            />
                        )}

                        <InsertionInfo activeTab={activeTab} />

                        <InsertionCodeField loading={loading} text={codeWithAllParams[activeTab]} />
                    </>
                )}
            </Flex>
        </div>
    );
}
